import { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Zap, Scale, Target, BarChart3, AlertTriangle, Info, ChevronDown, ChevronUp, Users, TrendingUp, TrendingDown } from 'lucide-react';
import SprintAnalytics from './SprintAnalytics';
import BalanceAnalytics from './BalanceAnalytics';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function AthleticAnalytics({ childId, childName, showTeamComparison = false }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [teamComparison, setTeamComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showTeamOverlay, setShowTeamOverlay] = useState(false);

  useEffect(() => {
    if (childId) {
      fetchAnalyticsOverview();
      if (showTeamComparison) {
        fetchTeamComparison();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, showTeamComparison]);

  const fetchAnalyticsOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/child/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsOverview(response.data);
    } catch (error) {
      console.error('Error fetching analytics overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamComparison = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/team-comparison/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamComparison(response.data);
    } catch (error) {
      console.error('Error fetching team comparison:', error);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'sprint', label: 'Sprint Analysis', icon: Zap },
    { id: 'balance', label: 'Balance Analysis', icon: Scale }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#02b5ff]"></div>
      </div>
    );
  }

  if (!analyticsOverview?.has_data) {
    return (
      <div className="text-center py-12">
        <Activity className="h-16 w-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-bold text-white mb-2">No Athletic Test Data</h3>
        <p className="text-zinc-500 max-w-md mx-auto">
          Upload athletic test results to see performance analytics with age-based benchmarks.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="athletic-analytics">
      {/* Header with Age Info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#02b5ff]" />
            Performance Analytics
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            {analyticsOverview.total_tests} tests recorded • Age group: {analyticsOverview.age_group} years
          </p>
        </div>
        
        {/* DOB Warning if default */}
        {analyticsOverview.is_dob_default && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <span className="text-xs text-amber-400">Default DOB detected - Update for accurate benchmarks</span>
          </div>
        )}
      </div>

      {/* Tab Navigation + Team Comparison Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#02b5ff] text-white'
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
                data-testid={`analytics-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Team Comparison Toggle */}
        {teamComparison?.has_team && (
          <button
            onClick={() => setShowTeamOverlay(!showTeamOverlay)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              showTeamOverlay
                ? 'bg-[#ff025f] text-white'
                : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
          >
            <Users className="h-4 w-4" />
            {showTeamOverlay ? 'Hide' : 'Show'} Team Comparison
          </button>
        )}
      </div>

      {/* Team Comparison Overlay */}
      {showTeamOverlay && teamComparison?.has_team && teamComparison.comparisons && (
        <div className="bg-gradient-to-r from-[#ff025f]/10 to-[#02b5ff]/10 border border-[#ff025f]/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-5 w-5 text-[#ff025f]" />
            <h3 className="text-lg font-bold text-white">
              Team Comparison: {teamComparison.team_name}
            </h3>
            <span className="text-xs bg-zinc-800 px-2 py-1 rounded text-zinc-400">
              {teamComparison.team_size} athletes
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(teamComparison.comparisons).slice(0, 9).map(([testName, data]) => (
              <div key={testName} className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-700/50">
                <p className="text-xs text-zinc-400 mb-2 truncate" title={testName}>{testName}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-white">{data.athlete_value?.toFixed(2)}</span>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    data.is_above_average 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {data.is_above_average ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {data.comparison_text}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>Team Avg: {data.team_avg}</span>
                  <span>Range: {data.team_min} - {data.team_max}</span>
                </div>
              </div>
            ))}
          </div>
          
          {Object.keys(teamComparison.comparisons).length > 9 && (
            <p className="text-center text-xs text-zinc-500 mt-4">
              +{Object.keys(teamComparison.comparisons).length - 9} more tests available
            </p>
          )}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-yellow-400" />
                <span className="text-xs text-zinc-400">Sprint Tests</span>
              </div>
              <div className="text-2xl font-bold text-white">{analyticsOverview.sprint_tests}</div>
            </div>
            
            <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-emerald-400" />
                <span className="text-xs text-zinc-400">Jump Tests</span>
              </div>
              <div className="text-2xl font-bold text-white">{analyticsOverview.jump_tests}</div>
            </div>
            
            <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-zinc-400">Agility Tests</span>
              </div>
              <div className="text-2xl font-bold text-white">{analyticsOverview.agility_tests}</div>
            </div>
            
            <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="h-4 w-4 text-purple-400" />
                <span className="text-xs text-zinc-400">Balance Tests</span>
              </div>
              <div className="text-2xl font-bold text-white">{analyticsOverview.balance_tests}</div>
            </div>
          </div>

          {/* Athlete Info Card */}
          <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
            <div className="flex items-center gap-3 mb-4">
              <Info className="h-5 w-5 text-[#02b5ff]" />
              <h3 className="text-lg font-bold text-white">Benchmark Information</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-zinc-300 mb-3">Athlete Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Name:</span>
                    <span className="text-white">{analyticsOverview.child_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Current Age:</span>
                    <span className="text-white">{analyticsOverview.current_age} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Age Group:</span>
                    <span className="text-[#02b5ff] font-medium">{analyticsOverview.age_group} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total Tests:</span>
                    <span className="text-white">{analyticsOverview.total_tests}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-zinc-300 mb-3">Age Group Benchmarks</h4>
                <p className="text-xs text-zinc-500 mb-3">
                  Performance is graded 1-5 based on age-appropriate standards:
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                    <span className="text-zinc-400">Grade 5:</span>
                    <span className="text-emerald-400">Excellent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="text-zinc-400">Grade 4:</span>
                    <span className="text-green-400">Good</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                    <span className="text-zinc-400">Grade 3:</span>
                    <span className="text-yellow-400">Average</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    <span className="text-zinc-400">Grade 2:</span>
                    <span className="text-orange-400">Below Average</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-400"></div>
                    <span className="text-zinc-400">Grade 1:</span>
                    <span className="text-red-400">Needs Improvement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Collapsible Sections for Overview */}
          <div className="space-y-4">
            {/* Sprint Section */}
            {analyticsOverview.sprint_tests > 0 && (
              <div className="bg-zinc-800/30 rounded-xl border border-zinc-700/50 overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === 'sprint' ? null : 'sprint')}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-yellow-400" />
                    <span className="font-medium text-white">Sprint Performance</span>
                    <span className="text-xs text-zinc-500">{analyticsOverview.sprint_tests} tests</span>
                  </div>
                  {expandedSection === 'sprint' ? (
                    <ChevronUp className="h-5 w-5 text-zinc-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-zinc-400" />
                  )}
                </button>
                {expandedSection === 'sprint' && (
                  <div className="px-4 pb-4">
                    <SprintAnalytics childId={childId} childName={childName} />
                  </div>
                )}
              </div>
            )}

            {/* Balance Section */}
            {analyticsOverview.balance_tests > 0 && (
              <div className="bg-zinc-800/30 rounded-xl border border-zinc-700/50 overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === 'balance' ? null : 'balance')}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Scale className="h-5 w-5 text-purple-400" />
                    <span className="font-medium text-white">Balance Analysis</span>
                    <span className="text-xs text-zinc-500">{analyticsOverview.balance_tests} bilateral tests</span>
                  </div>
                  {expandedSection === 'balance' ? (
                    <ChevronUp className="h-5 w-5 text-zinc-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-zinc-400" />
                  )}
                </button>
                {expandedSection === 'balance' && (
                  <div className="px-4 pb-4">
                    <BalanceAnalytics childId={childId} childName={childName} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Available Benchmarks */}
          {analyticsOverview.benchmarks && Object.keys(analyticsOverview.benchmarks).length > 0 && (
            <div className="bg-zinc-800/30 rounded-xl p-6 border border-zinc-700/50">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-[#02b5ff]" />
                Age Group Benchmarks ({analyticsOverview.age_group} years)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(analyticsOverview.benchmarks).map(([testName, benchmark]) => (
                  <div key={testName} className="bg-zinc-900/50 rounded-lg p-4">
                    <h4 className="font-medium text-zinc-300 mb-2">{testName}</h4>
                    <div className="space-y-1 text-xs">
                      {Object.entries(benchmark.grades).sort((a, b) => b[0] - a[0]).map(([grade, range]) => (
                        <div key={grade} className="flex justify-between">
                          <span className={`${
                            grade === '5' ? 'text-emerald-400' :
                            grade === '4' ? 'text-green-400' :
                            grade === '3' ? 'text-yellow-400' :
                            grade === '2' ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            Grade {grade}:
                          </span>
                          <span className="text-zinc-400">
                            {range.min}{range.max !== null ? ` - ${range.max}` : '+'} {benchmark.unit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sprint' && (
        <SprintAnalytics childId={childId} childName={childName} />
      )}

      {activeTab === 'balance' && (
        <BalanceAnalytics childId={childId} childName={childName} />
      )}
    </div>
  );
}
